// frontend/src/components/Chatbot/ActionResultsDisplay.tsx
// Shows the results of executed actions from the agentic loop.

import { useState } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronRight, Ban } from 'lucide-react';
import { getActionIcon } from './chatUtils';

export interface ExecutedAction {
  tool?: string;
  tool_use_id?: string;
  success?: boolean;
  description?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export interface BlockedAction {
  tool?: string;
  id?: string;
  description?: string;
  reason?: string;
  [key: string]: unknown;
}

interface ActionResultItemProps {
  action: ExecutedAction & BlockedAction;
  type: 'executed' | 'blocked';
}

const ActionResultItem = ({ action, type }: ActionResultItemProps) => {
  const Icon = getActionIcon(action.tool);
  const isSuccess = type === 'executed' && action.success !== false;
  const isBlocked = type === 'blocked';

  let colors: { bg: string; border: string; icon: string; badge: string };
  if (isBlocked) {
    colors = {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      icon: 'text-gray-400',
      badge: 'bg-gray-100 text-gray-600',
    };
  } else if (isSuccess) {
    colors = {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-500',
      badge: 'bg-green-100 text-green-700',
    };
  } else {
    colors = {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-500',
      badge: 'bg-red-100 text-red-700',
    };
  }

  return (
    <div
      className={`flex items-start gap-3 p-2.5 rounded-md ${colors.bg} border ${colors.border}`}
    >
      <div className={`flex-shrink-0 ${colors.icon}`}>
        {isBlocked ? (
          <Ban className="w-4 h-4" />
        ) : isSuccess ? (
          <CheckCircle className="w-4 h-4" />
        ) : (
          <XCircle className="w-4 h-4" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {action.tool || action.description}
          </span>
        </div>

        {action.message && <p className="text-xs text-gray-600 mt-0.5">{action.message}</p>}

        {action.error && <p className="text-xs text-red-600 mt-0.5">{action.error}</p>}

        {isBlocked && action.reason && (
          <p className="text-xs text-gray-500 mt-0.5">{action.reason}</p>
        )}
      </div>

      <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${colors.badge}`}>
        {isBlocked ? 'Blocked' : isSuccess ? 'Done' : 'Failed'}
      </span>
    </div>
  );
};

export interface ActionResultsDisplayProps {
  executedActions?: ExecutedAction[];
  blockedActions?: BlockedAction[];
  roundsUsed?: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const ActionResultsDisplay = ({
  executedActions = [],
  blockedActions = [],
  roundsUsed = 0,
  collapsible = true,
  defaultCollapsed = false,
}: ActionResultsDisplayProps) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const totalExecuted = executedActions.length;
  const successCount = executedActions.filter((a) => a.success !== false).length;
  const failedCount = totalExecuted - successCount;
  const blockedCount = blockedActions.length;

  if (totalExecuted === 0 && blockedCount === 0) {
    return null;
  }

  const summary: string[] = [];
  if (successCount > 0) summary.push(`${successCount} completed`);
  if (failedCount > 0) summary.push(`${failedCount} failed`);
  if (blockedCount > 0) summary.push(`${blockedCount} blocked`);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div
        className={`flex items-center justify-between px-3 py-2 bg-white border-b border-gray-100 ${
          collapsible ? 'cursor-pointer hover:bg-gray-50' : ''
        }`}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {successCount > 0 && (
              <span className="flex items-center text-green-600">
                <CheckCircle className="w-4 h-4" />
              </span>
            )}
            {failedCount > 0 && (
              <span className="flex items-center text-red-600">
                <XCircle className="w-4 h-4" />
              </span>
            )}
            {blockedCount > 0 && (
              <span className="flex items-center text-gray-400">
                <Ban className="w-4 h-4" />
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-gray-700">
            Actions: {summary.join(', ')}
          </span>
          {roundsUsed > 0 && (
            <span className="text-xs text-gray-400">
              ({roundsUsed} round{roundsUsed !== 1 ? 's' : ''})
            </span>
          )}
        </div>

        {collapsible && (
          <button className="p-1 hover:bg-gray-100 rounded">
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        )}
      </div>

      {!isCollapsed && (
        <div className="p-2 space-y-1.5">
          {executedActions.map((action, index) => (
            <ActionResultItem
              key={action.tool_use_id || `exec-${index}`}
              action={action as ExecutedAction & BlockedAction}
              type="executed"
            />
          ))}

          {blockedActions.map((action, index) => (
            <ActionResultItem
              key={action.id || `blocked-${index}`}
              action={action as ExecutedAction & BlockedAction}
              type="blocked"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionResultsDisplay;
